import React, { useState } from 'react';
import {
  Box,
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Alert,
  ProgressBar,
  FormField,
  Input,
  Select,
  Container,
  Table,
  AppLayout,
  Toggle,
  Textarea,
  Modal
} from '@amzn/awsui-components-core';
import { BaseballCardEmployee, GenerationProgress, GenerationResult } from 'src/api/interfaces';
import { fetchAllReportsUnderLeader } from 'src/api/app-sync-services';
import { fetchEmployeesBatch } from 'src/api/baseball-card-service';
import { BaseballCardStorageService } from 'src/services/BaseballCardStorageService';
import { captureAvatarImage, AvatarSizeType } from './utils/avatarCaptureUtils';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';

import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { BASEBALL_ADMIN_AVATAR_RESULTS_COLUMNS } from './utils/TableConfig';
import { logger } from 'src/logger';

const BaseballCardsAdminPage: React.FC = () => {
  const [leaderLogin, setLeaderLogin] = useState('');
  const [loginList, setLoginList] = useState('');
  const [stage, setStage] = useState<string>('alpha');
  const [employees, setEmployees] = useState<BaseballCardEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [forceRegenerate, setForceRegenerate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const stageOptions = [
    { label: 'Alpha', value: 'alpha' },
    { label: 'Beta', value: 'beta' },
    { label: 'Prod', value: 'prod' }
  ];

  const handleFetchEmployees = async () => {
    if (!leaderLogin.trim()) {
      setError('Please enter a leader login');
      return;
    }

    setLoading(true);
    setError(null);
    setEmployees([]);
    setResults([]);

    try {
      const allReportsData = await fetchAllReportsUnderLeader(leaderLogin);

      if (!Array.isArray(allReportsData)) {
        throw new Error('Unexpected data format from API');
      }

      const allAliases = allReportsData.map((emp) => emp.login_name).filter((alias) => alias && alias !== leaderLogin);
      const employeeList = await fetchEmployeesBatch(allAliases);

      setEmployees(employeeList);
      logger.info('Employee fetch completed', { count: employeeList.length });
    } catch (err: any) {
      logger.error('Failed to fetch employees');
      setError(`Failed to fetch employees: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFromLoginList = async () => {
    if (!loginList.trim()) {
      setError('Please enter at least one employee login');
      return;
    }

    setLoading(true);
    setError(null);
    setEmployees([]);
    setResults([]);

    try {
      const logins = loginList
        .split(',')
        .map((login) => login.trim().toLowerCase())
        .filter((login) => login.length > 0);

      if (logins.length === 0) {
        throw new Error('No valid logins found in the list');
      }

      const uniqueLogins = [...new Set(logins)];
      const employeeList = await fetchEmployeesBatch(uniqueLogins);

      if (employeeList.length === 0) {
        throw new Error('No valid employees found for the provided logins');
      }

      setEmployees(employeeList);
      logger.info('Employee fetch from login list completed', { count: employeeList.length });
    } catch (err: any) {
      logger.error('Failed to fetch employees from login list');
      setError(`Failed to fetch employees: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const generateEmployeeAvatar = async (
    employee: BaseballCardEmployee,
    size: AvatarSizeType,
    storageService: BaseballCardStorageService,
    forceRegen: boolean
  ): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      if (!forceRegen) {
        const existsResult = await storageService.checkAvatarExists(employee.login, size);
        if (existsResult.exists) {
          return { success: true, url: existsResult.url };
        }
      }

      const capturedImage = await captureAvatarImage({ userInfo: employee, size });
      return await storageService.uploadAvatar(employee.login, size, capturedImage.arrayBuffer);
    } catch (error: any) {
      return { success: false, error: error.message || 'Unknown error' };
    }
  };

  const generateBothSizesForEmployee = async (
    employee: BaseballCardEmployee,
    storageService: BaseballCardStorageService,
    forceRegen: boolean
  ): Promise<GenerationResult> => {
    const employeeName = `${employee.firstName} ${employee.lastName}`;

    const [largeResult, mediumResult] = await Promise.all([
      generateEmployeeAvatar(employee, 'large', storageService, forceRegen),
      generateEmployeeAvatar(employee, 'medium', storageService, forceRegen)
    ]);

    const success = largeResult.success && mediumResult.success;
    return {
      login: employee.login,
      name: employeeName,
      status: success ? 'success' : 'failed',
      error: !success ? largeResult.error || mediumResult.error : undefined,
      largeUrl: largeResult.url,
      mediumUrl: mediumResult.url
    };
  };

  const handleGenerateAvatars = async () => {
    if (employees.length === 0) {
      setError('No employees to generate avatars for');
      return;
    }

    setGenerating(true);
    setError(null);
    setResults([]);

    const storageService = new BaseballCardStorageService(stage);
    const generationResults: GenerationResult[] = [];

    setProgress({
      total: employees.length,
      completed: 0,
      failed: 0,
      status: 'in-progress'
    });

    for (const employee of employees) {
      const employeeName = `${employee.firstName} ${employee.lastName}`;
      setProgress((prev) => ({ ...prev!, currentEmployee: `Processing: ${employeeName}` }));

      const result = await generateBothSizesForEmployee(employee, storageService, forceRegenerate);
      generationResults.push(result);

      setProgress((prev) => ({
        ...prev!,
        completed: prev!.completed + 1,
        failed: result.status === 'failed' ? prev!.failed + 1 : prev!.failed
      }));

      setResults([...generationResults]);
    }

    setProgress((prev) => ({
      ...prev!,
      status: 'completed',
      currentEmployee: undefined
    }));

    setGenerating(false);
  };

  const handleClearAvatars = () => {
    if (employees.length === 0) {
      setError('No employees loaded. Please fetch employees first.');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmClearAvatars = async () => {
    setShowDeleteConfirm(false);
    setLoading(true);
    setError(null);

    try {
      const storageService = new BaseballCardStorageService(stage);
      let deletedCount = 0;

      for (const employee of employees) {
        const largeDeleted = await storageService.deleteAvatar(employee.login, 'large');
        const mediumDeleted = await storageService.deleteAvatar(employee.login, 'medium');

        if (largeDeleted) deletedCount++;
        if (mediumDeleted) deletedCount++;
      }

      setSuccessMessage(`Successfully deleted ${deletedCount} avatar(s)`);
      setResults([]);
    } catch (err: any) {
      setError(`Failed to delete avatars: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout
      navigation={<HomePageSideNavigationV2 />}
      navigationOpen={navigationOpen}
      onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
      contentType="default"
      toolsHide
      content={
        <ContentLayout header={<Header variant="h1">Baseball Cards Avatar Admin</Header>}>
          <SpaceBetween size="l">
            {error && (
              <Alert type="error" dismissible onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert type="success" dismissible onDismiss={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}

            <Modal
              visible={showDeleteConfirm}
              onDismiss={() => setShowDeleteConfirm(false)}
              header="Confirm Deletion"
              footer={
                <Box float="right">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => setShowDeleteConfirm(false)}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmClearAvatars}>
                      Delete
                    </Button>
                  </SpaceBetween>
                </Box>
              }
            >
              Are you sure you want to delete avatars for {employees.length} employee(s)? This action cannot be undone.
            </Modal>

            <Container header={<Header variant="h2">Configuration</Header>}>
              <SpaceBetween size="m">
                <FormField label="Leader Login" description="Enter the login of the leader whose team you want to generate avatars for">
                  <SpaceBetween direction="horizontal" size="s">
                    <Input
                      value={leaderLogin}
                      onChange={(event) => setLeaderLogin(event.detail.value)}
                      placeholder="e.g., jsmith"
                      disabled={loading || generating}
                    />
                    <Button onClick={handleFetchEmployees} loading={loading} disabled={generating || !leaderLogin.trim()}>
                      Fetch Team
                    </Button>
                  </SpaceBetween>
                </FormField>

                <Box textAlign="center" color="text-body-secondary">
                  — OR —
                </Box>

                <FormField label="Employee Login List" description="Paste comma-separated employee logins to process specific employees">
                  <SpaceBetween size="s">
                    <Textarea
                      value={loginList}
                      onChange={(event) => setLoginList(event.detail.value)}
                      placeholder="e.g., jsmith, mjones, klee, agarcia"
                      disabled={loading || generating}
                      rows={3}
                    />
                    <Button onClick={handleFetchFromLoginList} loading={loading} disabled={generating || !loginList.trim()}>
                      Fetch from Login List
                    </Button>
                  </SpaceBetween>
                </FormField>

                <FormField label="Environment Stage" description="Select the stage environment for avatar storage">
                  <Select
                    selectedOption={stageOptions.find((opt) => opt.value === stage) || stageOptions[0]}
                    onChange={(event) => setStage(event.detail.selectedOption.value || 'alpha')}
                    options={stageOptions}
                    disabled={loading || generating}
                  />
                </FormField>

                <FormField label="Force Regenerate" description="When enabled, regenerates all avatars even if they already exist in S3">
                  <Toggle checked={forceRegenerate} onChange={(event) => setForceRegenerate(event.detail.checked)} disabled={loading || generating}>
                    {forceRegenerate ? 'Enabled - Will overwrite existing avatars' : 'Disabled - Skip existing avatars'}
                  </Toggle>
                </FormField>

                {employees.length > 0 && (
                  <Box>
                    <SpaceBetween direction="horizontal" size="s">
                      <Button variant="primary" onClick={handleGenerateAvatars} loading={generating} disabled={loading}>
                        Generate Avatars ({employees.length})
                      </Button>
                      <Button onClick={handleClearAvatars} disabled={loading || generating}>
                        Clear All Avatars
                      </Button>
                    </SpaceBetween>
                  </Box>
                )}
              </SpaceBetween>
            </Container>

            {employees.length > 0 && (
              <Container header={<Header variant="h3">Found {employees.length} employee(s)</Header>}>
                <Box>{employees.map((emp) => `${emp.firstName} ${emp.lastName}`).join(', ')}</Box>
              </Container>
            )}

            {progress && (
              <Container header={<Header variant="h2">Generation Progress</Header>}>
                <SpaceBetween size="m">
                  <ProgressBar
                    value={(progress.completed / progress.total) * 100}
                    additionalInfo={progress.currentEmployee || 'Completed'}
                    label={`Processing: ${progress.completed} / ${progress.total}`}
                  />
                  <Box>
                    <strong>Failed:</strong> {progress.failed}
                  </Box>
                </SpaceBetween>
              </Container>
            )}

            {results.length > 0 && (
              <Container header={<Header variant="h2">Results ({results.length})</Header>}>
                <Table columnDefinitions={BASEBALL_ADMIN_AVATAR_RESULTS_COLUMNS} items={results} sortingDisabled={false} variant="embedded" />
              </Container>
            )}
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
};

export default BaseballCardsAdminPage;
